import React from "react";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import SolutionsPageTemplate from "../../components/SolutionsPageTemplate/SolutionsPageTemplate";

const SEO_TITLE = "Accounting Firm Productivity and Billable Time Software | TeamGrid";
const SEO_DESCRIPTION = "Capture every billable hour automatically. Optimize utilization, prevent burnout and improve client profitability with full visibility.";
const SEO_CANONICAL = "https://teamgrid.ai/solutions/accounting-firm-productivity";
const OG_IMAGE = "https://teamgrid.ai/og/og-home.webp";

const AccountingFirms = () => {
  const navigate = useNavigate();
  // FAQ data specific to Accounting Firms
  const accountingFAQs = [
    {
      question:
        "How does TeamGrid improve billable time accuracy for accounting teams?",
      answer:
        "TeamGrid automatically tracks and categorizes work to capture billable hours accurately across clients, reducing underreported time and revenue leakage. This improves utilization visibility for accounting productivity software.",
    },
    {
      question: "Does TeamGrid replace timesheets for accounting teams?",
      answer:
        "Yes, TeamGrid removes manual timesheets by auto-capturing work activity, making it easier for firms to manage productive billing without manual entry.",
    },
    {
      question: "Is TeamGrid suitable for mid-sized accounting firms?",
      answer:
        "Yes, TeamGrid scales with firm size and helps mid-sized accounting teams improve productivity and billable time capture across client work.",
    },
  ];

  // Productivity cards data for Accounting Firms
  const productivityCards = [
    {
      title: "Billable Time Capture & Utilization",
      description:
        "Accountants juggle multiple clients daily, causing significant underreported billable work.",
      points: [
        "Automatically captures client-level billable work",
        "Distinguishes billable vs non-billable activities",
        "Shows real-time utilization vs firm targets",
        "Identifies recoverable, unlogged billable time",
      ],
    },
    {
      title: "Seasonal Capacity & Burnout Risk",
      description:
        "Tax season creates extreme workload spikes that strain teams and margins.",
      points: [
        "Forecasts peak-season demand using historical data",
        "Balances workloads across staff and seasonal hires",
        "Flags sustained overwork before burnout occurs",
        "Optimizes staffing mix across the year",
      ],
    },
    {
      title: "Accuracy Protection Under Pressure",
      description: "Speed pressure increases error risk during busy periods.",
      points: [
        "Detects overwork patterns linked to errors",
        "Tracks review cycle adequacy before deadlines",
        "Flags rushed engagements needing extra review",
        "Protects quality without slowing delivery",
      ],
    },
    {
      title: "Client Profitability & Scope Control",
      description:
        "Some clients quietly consume far more time than they pay for.",
      points: [
        "Compares time invested vs engagement fees",
        "Identifies scope creep early",
      ],
    },
  ];

  // Custom features for Accounting Firms (6 features)
  const customFeatures = [
    {
      img: "/complete_feature_image1.webp",
      title: "Automatic Time Capture",
      desc: "Capture billable hours automatically across clients and matters.",
    },
    {
      img: "/completefeature_img2.webp",
      title: "Privacy-First Tracking",
      desc: "Silent tracking that respects privacy while capturing accurate time.",
    },
    {
      img: "/completefeature_image3.webp",
      title: "Utilization Monitoring",
      desc: "Real-time visibility into team billable performance.",
    },
    {
      img: "/completefeature_image4.webp",
      title: "Client-Level Intelligence",
      desc: "Deep insights into client profitability and time allocation.",
    },
    {
      img: "/completefeture_image5.webp",
      title: "Seasonal Capacity Planning",
      desc: "Predict and manage peak season workloads intelligently.",
    },
    {
      img: "/completefeature_image6.webp",
      title: "Org-Wide Productivity Intelligence",
      desc: "Firm-wide productivity benchmarking and insights.",
    },
  ];

  // Custom outcomes for Accounting Firms
  const customOutcomes = [
    {
      percentage: "22%",
      title: "HIGHER BILLABLE UTILIZATION",
      desc: "Capture work that manual timesheets miss. Upto 22% increase in reported billable hours.",
    },
    {
      percentage: "12%",
      title: "REVENUE REALIZATION",
      desc: "Recover underreported billable work without increasing team workload.",
    },
    {
      percentage: "38%",
      title: "SUSTAINABLE TAX SEASONS",
      desc: "Protect teams while meeting deadlines. 38% reduction in peak-season overtime.",
    },
    {
      percentage: "15%",
      title: "IMPROVED CLIENT MARGINS",
      desc: "Price based on real effort, not assumptions. 15%+ margin improvement.",
    },
  ];

  const customButton = (
    <motion.button
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      onClick={() => navigate("/book-demo")}
      className="flex items-center justify-center px-7 py-3.5 rounded-[14px] font-semibold text-black transition-all"
      style={{
        background: "#00D1C1",
        boxShadow: "0px 18px 40px rgba(0, 209, 193, 0.45)",
      }}
    >
      Get a Demo
    </motion.button>
  );

  return (
    <>
      <Helmet>
        <title>{SEO_TITLE}</title>
        <meta name="description" content={SEO_DESCRIPTION} />
        <link rel="canonical" href={SEO_CANONICAL} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={SEO_CANONICAL} />
        <meta property="og:title" content={SEO_TITLE} />
        <meta property="og:description" content={SEO_DESCRIPTION} />
        <meta property="og:image" content={OG_IMAGE} />
        <meta property="og:site_name" content="TeamGrid" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={SEO_CANONICAL} />
        <meta name="twitter:title" content={SEO_TITLE} />
        <meta name="twitter:description" content={SEO_DESCRIPTION} />
        <meta name="twitter:image" content={OG_IMAGE} />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: [
            { "@type": "Question", name: "How does TeamGrid improve billable time accuracy for accounting teams?", acceptedAnswer: { "@type": "Answer", text: "TeamGrid automatically tracks and categorizes work to improve billable time accuracy across clients, reducing underreported hours and revenue leakage while improving utilization visibility." } },
            { "@type": "Question", name: "Does TeamGrid replace timesheets for accounting teams?", acceptedAnswer: { "@type": "Answer", text: "Yes, TeamGrid eliminates manual timesheets by automatically capturing work activity, helping accounting teams manage billing more accurately without manual entry." } },
            { "@type": "Question", name: "Is TeamGrid suitable for mid-sized accounting firms?", acceptedAnswer: { "@type": "Answer", text: "Yes, TeamGrid is designed to scale with firm size and helps mid-sized accounting firms improve productivity and billable time visibility across client work." } },
          ],
        })}</script>
      </Helmet>
      <SolutionsPageTemplate
      breadcrumbTitle="Accounting Firms"
      heroTitle="Accounting Firm<br/>Productivity Software <span style='color: #00D1C1; font-weight: 600;'>to</span><br/><span style='color: #00D1C1; font-weight: 600;'>Capture Every Billable Hour.</span>"
      heroSubtitle="Maximize Billable Hours Without Compromising Accuracy"
      primaryButtonText="Get a Demo for Your Accounting Firm"
      customPrimaryButton={customButton}
      productivityTitle="Key Productivity Areas"
      productivitySubtitle="Understand exactly where time goes and how to optimize billable work without micromanagement."
      featureSuiteTitle="Complete Feature Suite"
      featureSuiteSubtitle="Everything you need in an accounting productivity & time tracking platform"
      customFeatures={customFeatures}
      outcomesTitle="Outcomes You Can Expect"
      outcomesSubtitle={null}
      customOutcomes={customOutcomes}
      whyChooseTitle="Why Accounting Firms Choose TeamGrid"
      whyChooseDescription="TeamGrid is more than time tracking for accountants. It's a productivity intelligence platform built to maximize billable hours, protect accuracy, and optimize client profitability without micromanagement."
      whyChooseButtonText="See TeamGrid in Action"
      productivityCards={productivityCards}
      productivityCardIcons={[
        "/EdTech_icon1.webp",
        "/Edtech_icon2.webp",
        "/Edteh_Icon3.webp",
        "/Edtech_icon4.webp",
      ]}
      faqs={accountingFAQs}
      faqSubtitle={null}
      faqBackgroundColor="#F3F4F6"
    />
    </>
  );
};

export default AccountingFirms;
